import { AppState } from "@app/store/reducers/app.reducers";
import { selectApp } from "@app/store/selectors/app/app.selectors";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { NestedCommentInterface } from "@core/interfaces/nested-comment.interface";
import { createSelector } from "@ngrx/store";

export const selectNestedComments = createSelector(
  selectApp,
  (state: AppState): NestedCommentInterface[] => state.nestedComments
);

export const selectNestedCommentsByContentTypeIdAndObjectId = (
  contentTypeId: ContentTypeInterface["id"],
  objectId: number,
  category?: string
) =>
  createSelector(selectNestedComments, nestedComments => {
    if (nestedComments === null || nestedComments === undefined) {
      return null;
    }

    let filteredComments = nestedComments.filter(
      nestedComment => nestedComment.contentType === contentTypeId && nestedComment.objectId === objectId
    );

    if (category) {
      // If category is specified, filter by that category
      filteredComments = filteredComments.filter(comment => comment.category === category);
    } else {
      // If no category is specified, only return comments with no category
      filteredComments = filteredComments.filter(
        comment => comment.category === undefined || comment.category === null
      );
    }

    return filteredComments;
  });

export const selectNestedCommentById = createSelector(
  selectNestedComments,
  (nestedComments: NestedCommentInterface[], id: NestedCommentInterface["id"]): NestedCommentInterface | null => {
    if (nestedComments === null || nestedComments === undefined) {
      return null;
    }

    const matching = nestedComments.filter(nestedComment => nestedComment.id === id);

    if (matching && matching.length > 0) {
      return matching[0];
    }

    return null;
  }
);

export const selectNestedCommentCategoriesByContentTypeIdAndObjectId = (
  contentTypeId: ContentTypeInterface["id"],
  objectId: number
) =>
  createSelector(selectNestedComments, nestedComments => {
    if (nestedComments === null || nestedComments === undefined) {
      return [];
    }

    const relevantComments = nestedComments.filter(
      nestedComment => nestedComment.contentType === contentTypeId && nestedComment.objectId === objectId
    );

    // Get unique categories and filter out null/undefined
    const categories = new Set(
      relevantComments.map(comment => comment.category).filter(category => category !== undefined && category !== null)
    );

    return Array.from(categories);
  });
